// ============================================================
// shared/consigneBrut.js — le brut que le DEVISEUR annonce.
// ------------------------------------------------------------
// Le §8.6 fait écrire au deviseur comment il usinerait la pièce. Il y
// écrit très souvent d'où il PART :
//
//   « je pars d'un brut de 510x110x100 »
//   « je pars d'un étiré 40×10, prise 1 en étau sur le petit côté »
//   « barre Ø80, débitée à 120 »
//
// Jusqu'au 21 août 2026 cette phrase était stockée, envoyée à l'agent
// d'estimation… et le calcul du brut l'ignorait complètement. Le brut
// affiché restait celui déduit des cotes de la pièce finie — donc le
// volume, la masse et le prix matière aussi.
//
// ── POURQUOI EN CODE ET PAS PAR LE MODÈLE ──
//
// « 510x110x100 » est une suite de nombres dans une chaîne. La lire est
// un décodage, pas un jugement : c'est calculable, donc ça se calcule
// (§2bis, règle 1). Un modèle rendrait une valeur plausible et non
// reproductible, sur une grandeur qui entre directement dans un prix.
//
// ── POURQUOI LA CONSIGNE GAGNE ──
//
// « Une seule porte par valeur » (§2bis). Le prix matière se changeait
// autrefois à trois endroits sans qu'aucun écran ne dise lequel
// gagnait ; on ne recommence pas avec le brut. La consigne est une
// phrase que le deviseur a ÉCRITE : c'est la provenance `saisi`, la plus
// haute. Elle prime donc sur la déduction — et l'écran le DIT, avec le
// fragment exact qui l'a produite, pour que le remplacement ne soit
// jamais silencieux (§8.6).
//
// ── CE QU'IL NE FAIT PAS ──
//
// Il ne devine pas. Un fragment qu'il ne reconnaît pas ne produit rien
// plutôt qu'une cote approchée : c'est la même règle que
// `parseEncombrement`, et pour la même raison — une cote inventée
// produit un prix qui a l'air juste.
// ============================================================

/** Un nombre décimal, virgule ou point. */
const NB = '(\\d{1,5}(?:[.,]\\d{1,3})?)';
const num = (v) => Number(String(v).replace(',', '.'));

/**
 * Trois cotes collées par un séparateur de multiplication.
 * « 510x110x100 », « 510 × 110 × 100 », « 510*110*100 ».
 */
const PRISME = new RegExp(`${NB}\\s*[x×*]\\s*${NB}\\s*[x×*]\\s*${NB}`, 'i');

/**
 * Deux cotes : une SECTION, pas une pièce. « un étiré 40×10 » dit ce
 * qu'on achète, pas ce qu'on débite — la longueur vient de la pièce.
 */
const SECTION = new RegExp(`${NB}\\s*[x×*]\\s*${NB}`, 'i');

/** Un diamètre, et sa longueur débitée quand elle est donnée. */
const ROND = new RegExp(`(?:Ø|ø|\\bD|\\bdiam[èe]tre\\s*(?:de\\s*)?)\\s*${NB}`, 'i');
const LONGUEUR = new RegExp(`(?:\\bL|\\blong(?:ueur)?\\s*(?:de\\s*)?|d[ée]bit[ée]e?\\s*(?:à|a)\\s*)\\s*${NB}`, 'i');

/**
 * Décode le brut annoncé dans une consigne en langage naturel.
 *
 * @param {string|null} texte  la phrase du deviseur, telle qu'il l'a écrite
 * @returns {{trouve: boolean, shape: string|null, cotes: object|null,
 *            fragment: string|null, partiel: boolean}}
 *   `partiel` : une SECTION seule a été donnée ; la longueur reste celle
 *   de la pièce. L'appelant doit la fournir.
 */
export function brutDeLaConsigne(texte) {
  const vide = { trouve: false, shape: null, cotes: null, fragment: null, partiel: false };
  const brut = String(texte ?? '').trim();
  if (!brut) return vide;

  // ── Le ROND d'abord ──
  // « Ø80 » et « 80x40 » ne se confondent pas, mais « barre Ø80 débitée
  // à 120 » contient un nombre isolé qui n'est pas une multiplication :
  // chercher le prisme en premier ne le trouverait pas, et chercher le
  // rond en premier évite qu'un « D » de désignation matière soit lu
  // comme un prisme. On teste donc le rond avant, et on n'y entre que
  // s'il n'y a AUCUNE multiplication — sinon « 40x10 » gagnerait un Ø.
  if (!SECTION.test(brut)) {
    const r = brut.match(ROND);
    if (r && num(r[1]) > 0) {
      const l = brut.match(LONGUEUR);
      return {
        trouve: true,
        shape: 'a_la_cote',
        cotes: {
          diametre_mm: num(r[1]),
          longueur_mm: l && num(l[1]) > 0 ? num(l[1]) : null,
          largeur_mm: null,
          epaisseur_mm: null,
        },
        fragment: l ? `${r[0]} ${l[0]}` : r[0],
        partiel: !(l && num(l[1]) > 0),
      };
    }
    return vide;
  }

  // ── TROIS cotes : la pièce brute entière ──
  const p = brut.match(PRISME);
  if (p) {
    const [a, b, c] = [num(p[1]), num(p[2]), num(p[3])];
    if (a > 0 && b > 0 && c > 0) {
      // Non ordonnées ici : `cotesBrut` ordonne, et c'est LUI qui doit le
      // faire — sinon deux endroits décident de la même convention.
      return {
        trouve: true,
        shape: 'a_la_cote',
        cotes: { diametre_mm: null, longueur_mm: a, largeur_mm: b, epaisseur_mm: c },
        fragment: p[0],
        partiel: false,
      };
    }
  }

  // ── DEUX cotes : une section ──
  const sec = brut.match(SECTION);
  if (sec) {
    const [a, b] = [num(sec[1]), num(sec[2])];
    if (a > 0 && b > 0) {
      return {
        trouve: true,
        shape: 'a_la_cote',
        cotes: { diametre_mm: null, longueur_mm: null, largeur_mm: a, epaisseur_mm: b },
        fragment: sec[0],
        partiel: true,
      };
    }
  }

  return vide;
}

/**
 * Le brut de la consigne, complété par la pièce quand il est partiel.
 *
 * Une section seule (« étiré 40×10 ») ne dit pas ce qu'on débite : la
 * longueur reste celle de la pièce. La rendre nulle ferait retomber le
 * calcul sur le refus « cote absente » — juste, et inutilement, puisque
 * la longueur est connue.
 */
export function appliquerConsigneAuBrut(consigne, encombrementPiece = {}) {
  const lu = brutDeLaConsigne(consigne);
  if (!lu.trouve) return { ...lu, encombrement: null };

  const c = { ...lu.cotes };
  if (lu.partiel) {
    if (c.diametre_mm != null) {
      c.longueur_mm = c.longueur_mm
        ?? encombrementPiece.epaisseur_mm ?? encombrementPiece.longueur_mm ?? null;
    } else {
      c.longueur_mm = encombrementPiece.longueur_mm ?? null;
    }
  }
  return { ...lu, cotes: c, encombrement: c };
}

/**
 * LA PIÈCE, AVEC LE BRUT QUE LA CONSIGNE ANNONCE — 27 août 2026
 *
 * Ce bloc vivait dans `devis.service.priceOnePiece`, c'est-à-dire au
 * CHIFFRAGE, et nulle part ailleurs. Conséquence mesurée le 27 août :
 * un deviseur qui écrit « je pars d'un brut à la cote de 545×310×30 »
 * et clique « Réviser » — le bouton est juste sous le champ — envoyait
 * à l'agent une fiche disant `barreau 540×150×140`.
 *
 * > **La phrase disait le contraire de la fiche technique jointe.**
 *
 * L'agent a suivi la phrase, parce que le prompt lui donne autorité :
 * mesuré, aucun débit sur deux passes. Mais on ne construit pas sur un
 * arbitrage. Et pendant ce temps la masse, les copeaux et le prix
 * matière se calculaient sur l'ancien brut, à l'écran comme au PDF,
 * jusqu'au prochain enregistrement.
 *
 * La fonction est ici, dans `shared/`, parce que TROIS endroits en ont
 * besoin et qu'il ne peut y en avoir qu'une implémentation : le
 * chiffrage serveur, l'écran, et le contexte envoyé à l'agent.
 *
 * Elle est PURE : elle rend une copie enrichie, elle n'écrit rien. La
 * consigne reste la seule porte pour ces cotes — pour les changer, on
 * change la phrase.
 *
 * @param {object} piece
 * @param {object} cotesPiece  les cotes de la pièce finie, pour le cas
 *   où la consigne ne donne qu'une section (« un étiré 40×10 »)
 * @param {function} cotesBrut la fonction de `shared/brut.js`, injectée
 *   pour ne pas créer de dépendance circulaire entre les deux modules
 * @param {object} settings
 * @returns {{piece: object, applique: boolean, fragment: string|null}}
 */
export function pieceAvecBrutDeConsigne(piece, cotesPiece, cotesBrut, settings = {}) {
  const decode = appliquerConsigneAuBrut(piece?.consigne_gamme, cotesPiece ?? {});
  if (!decode.trouve) return { piece, applique: false, fragment: null };

  const variante = cotesBrut('a_la_cote', decode.cotes, settings, {});
  // Un décodage qui ne produit pas de brut calculable ne remplace
  // rien : mieux vaut le brut déduit que pas de brut du tout, et le
  // refus reste DIT plutôt qu'appliqué en silence (invariant 26).
  if (variante.manque_cote) return { piece, applique: false, fragment: decode.fragment ?? null };

  return {
    piece: {
      ...piece,
      raw_shape: 'a_la_cote',
      raw_diameter: variante.diametre_mm,
      raw_l: variante.longueur_mm,
      raw_w: variante.largeur_mm,
      raw_h: variante.epaisseur_mm,
      raw_length: variante.longueur_mm,
    },
    applique: true,
    fragment: decode.fragment ?? null,
  };
}

/**
 * La description d'usinage, complétée par une CORRECTION de révision qui
 * annonce un brut — ou `{}` s'il n'y a rien à reporter. Pure.
 *
 * Devis 2026-10-015 : « on part d'un bloc de 130x130x70 » écrit en
 * correction a servi UNE fois à l'agent, puis rien : le brut de la pièce
 * se décode de la case de consigne, et la correction n'y était pas. La
 * pièce gardait un brut incalculable.
 *
 * On AJOUTE la phrase à la fin de la description, visible et modifiable —
 * on ne réécrit jamais ce que le deviseur a écrit. Si la description
 * annonce déjà un brut, on ne touche à rien : deux bruts dans une case ne
 * se départagent pas, et c'est au deviseur de choisir.
 */
export function consigneAvecBrutDeCorrection(description, correction) {
  if (!brutDeLaConsigne(correction).trouve) return {};
  if (brutDeLaConsigne(description).trouve) return {};
  const d = String(description ?? '').trim();
  return { consigne_gamme: d ? `${d}\n${String(correction).trim()}` : String(correction).trim() };
}
